/**
 * Store operational-hours + not-operational gate (Business Rule #38).
 *
 * Mirrors backend/services/store_hours.py — the two MUST stay in sync because
 * the customer surfaces (Landing banner, Cart/Checkout disabled button +
 * popup) compute open/closed locally for instant paint while the backend
 * re-evaluates the SAME rule at order creation (the server hard-stop).
 *
 * Pure JS, no fetches — caller supplies the branch object (which carries
 * `accepting_orders` + `operating_hours`). GET /branches/:id/status is the
 * server-side authority for the exact `opens_at`; this mirror is the fast paint.
 *
 * Precedence for a given branch + now (IST):
 *   1. accepting_orders === false          → not_accepting ("serving soon")
 *   2. operating_hours set + now outside
 *      every applicable slot                → closed ("Opens at <next opening>")
 *   3. operating_hours null / empty          → open (legacy branches always-open)
 *   4. otherwise                            → open
 *
 * `is_active` (fully hide the branch) is NOT handled here. `business_day_end_time`
 * (Rista accounting) is deliberately NOT consulted — open/closed is driven purely
 * by operating_hours slots (which encode past-midnight via close < open).
 *
 * The slot helpers (nowInIst / pyWeekday / parseHHMM / slotsForWeekday /
 * format12h / isOpenByHours / nextOpening / opensLabel) are exported so
 * utils/productGroupHours.js (Business Rule #52) evaluates a product group's
 * window with the SAME math — never a second copy of it.
 */

export const OPEN = 'open';
export const NOT_ACCEPTING = 'not_accepting';
export const CLOSED = 'closed';
export const NO_HOURS = 'no_hours';

export const NOT_ACCEPTING_MESSAGE = 'We are not operational right now. We will be serving soon.';

const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Monday=0
const MAX_LOOKAHEAD_DAYS = 7;

// IST is UTC+5:30 — return a Date whose UTC fields read as the IST wall clock,
// so getUTCDay()/getUTCHours() give IST values (same as bogoEligibility.js).
// Epoch ms is timezone-independent, so the shift is a constant +5:30 — do NOT
// add getTimezoneOffset() here: combined with getUTC* readers it cancels the
// shift on an IST device (yielding UTC, 5.5h behind — every store looked
// closed during open hours for customers in India).
export function nowInIst() {
  return new Date(Date.now() + (5 * 60 + 30) * 60_000);
}

// JS getUTCDay: 0=Sun..6=Sat → Python weekday(): 0=Mon..6=Sun.
export function pyWeekday(istDate) {
  return (istDate.getUTCDay() + 6) % 7;
}

export function parseHHMM(value) {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value).trim());
  if (!m) return null;
  const hh = +m[1];
  const mm = +m[2];
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

export function slotsForWeekday(operatingHours, weekday) {
  if (!operatingHours) return [];
  const slots = operatingHours[String(weekday)];
  return Array.isArray(slots) ? slots : [];
}

// Minutes in a day. A close of "23:59" is how the admin editor and every prod
// branch say "till midnight" (the next day's small hours sit in a separate
// `00:00-HH:MM` tail slot), so it is normalised to END_OF_DAY: the clock's last
// minute is INSIDE the window. Read literally, `nowMin < 1439` made both stores
// that close at "23:59" read CLOSED for the whole 23:59 minute, the next opening
// then resolved to the 00:00 tail and the Cart said "Opens tomorrow at
// 12:00 AM" — a Nahar customer at 23:59 on 2026-09-26 was refused checkout
// (the server gate had the same hole). Mirror of `_close_minutes` in
// backend/services/store_hours.py.
export const END_OF_DAY = 24 * 60;

export function closeMinutes(slot) {
  const c = parseHHMM(slot?.close);
  if (c == null) return null;
  return c === END_OF_DAY - 1 ? END_OF_DAY : c;
}

// Does the slot run up to (or past) midnight? True for a "23:59" close and for
// an overnight slot (close < open). A `00:00` slot on the FOLLOWING day is then
// a continuation of this one, not an opening — `nextOpening` skips it so the
// label never reads "Opens tomorrow at 12:00 AM" for a store that closes at
// midnight. Mirror of `_runs_to_midnight` in backend/services/store_hours.py.
export function slotRunsToMidnight(slot) {
  const o = parseHHMM(slot?.open);
  const c = closeMinutes(slot);
  if (o == null || c == null || o === c) return false;
  return c === END_OF_DAY || c < o;
}

function continuesPreviousDay(operatingHours, day) {
  const prev = new Date(day);
  prev.setUTCDate(day.getUTCDate() - 1);
  return slotsForWeekday(operatingHours, pyWeekday(prev)).some(slotRunsToMidnight);
}

export function format12h(minutes) {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  const suffix = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 || 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${suffix}`;
}

export function isOpenByHours(branch, nowIst) {
  const operatingHours = branch?.operating_hours;
  if (!operatingHours) return true;
  const nowMin = nowIst.getUTCHours() * 60 + nowIst.getUTCMinutes();

  // Today's slots. `closeMinutes` reads a "23:59" close as END_OF_DAY, so the
  // 23:59 minute is open (the last minute of the day is inside the window).
  for (const slot of slotsForWeekday(operatingHours, pyWeekday(nowIst))) {
    const o = parseHHMM(slot.open);
    const c = closeMinutes(slot);
    if (o == null || c == null || o === c) continue;
    if (o < c) {
      if (nowMin >= o && nowMin < c) return true;
    } else if (nowMin >= o) {
      // overnight — evening portion of today
      return true;
    }
  }

  // Yesterday's overnight slots spilling into this morning.
  const yesterday = new Date(nowIst);
  yesterday.setUTCDate(nowIst.getUTCDate() - 1);
  for (const slot of slotsForWeekday(operatingHours, pyWeekday(yesterday))) {
    const o = parseHHMM(slot.open);
    const c = closeMinutes(slot);
    if (o == null || c == null || o === c) continue;
    if (c < o && nowMin < c) return true; // morning portion carried from yesterday
  }

  return false;
}

export function nextOpening(branch, nowIst) {
  const operatingHours = branch?.operating_hours;
  if (!operatingHours) return null;
  for (let dayOffset = 0; dayOffset <= MAX_LOOKAHEAD_DAYS; dayOffset++) {
    const day = new Date(nowIst);
    day.setUTCDate(nowIst.getUTCDate() + dayOffset);
    let best = null;
    const continues = continuesPreviousDay(operatingHours, day);
    for (const slot of slotsForWeekday(operatingHours, pyWeekday(day))) {
      const o = parseHHMM(slot.open);
      if (o == null) continue;
      // A 00:00 slot after a day that ran to midnight is the SAME window
      // continuing, not the store opening — never the "opens at" moment.
      if (o === 0 && continues) continue;
      const opening = new Date(day);
      opening.setUTCHours(Math.floor(o / 60), o % 60, 0, 0);
      if (opening.getTime() > nowIst.getTime() && (best == null || opening < best)) {
        best = opening;
      }
    }
    if (best) return best;
  }
  return null;
}

export function opensLabel(opening, nowIst) {
  if (!opening) return null;
  const timeStr = format12h(opening.getUTCHours() * 60 + opening.getUTCMinutes());
  const nowDay = Date.UTC(nowIst.getUTCFullYear(), nowIst.getUTCMonth(), nowIst.getUTCDate());
  const candDay = Date.UTC(opening.getUTCFullYear(), opening.getUTCMonth(), opening.getUTCDate());
  const dayDiff = Math.round((candDay - nowDay) / 86_400_000);
  if (dayDiff <= 0) return `Opens at ${timeStr}`;
  if (dayDiff === 1) return `Opens tomorrow at ${timeStr}`;
  return `Opens ${DAY_ABBR[pyWeekday(opening)]} at ${timeStr}`;
}

/**
 * Resolve a branch's live availability (Business Rule #38).
 * Returns `{ open, reason, message, opensLabel }`.
 */
export function storeStatus(branch, now) {
  const n = now || nowInIst();

  if (branch?.accepting_orders === false) {
    return { open: false, reason: NOT_ACCEPTING, message: NOT_ACCEPTING_MESSAGE, opensLabel: null };
  }

  if (!branch?.operating_hours) {
    return { open: true, reason: NO_HOURS, message: '', opensLabel: null };
  }

  if (isOpenByHours(branch, n)) {
    return { open: true, reason: OPEN, message: '', opensLabel: null };
  }

  const opening = nextOpening(branch, n);
  const label = opensLabel(opening, n);
  return {
    open: false,
    reason: CLOSED,
    message: label || 'The store is currently closed.',
    opensLabel: label,
  };
}

/** Thin wrapper — true iff the branch would accept an order right now. */
export function isStoreOpenNow(branch, now) {
  return storeStatus(branch, now).open;
}

/**
 * Should Checkout refuse to create the order? (Business Rule #38)
 *
 * The ONE rule that decides whether a customer is stopped on the revenue path,
 * kept pure and tested because getting it wrong costs real orders:
 *
 *   1. Admins never blocked (mirrors the server gate).
 *   2. **Blocked ONLY on a server-confirmed closure.** If the server hasn't
 *      answered — `GET /branches/:id/status` still in flight, or it failed — we
 *      FAIL OPEN and let `create_order` decide. That hard-stop runs the same rule
 *      server-side and rejects before any payment is taken, so failing open can
 *      never take money for an order the store won't make.
 *
 * Rule 2 exists because a client-side guess must never be able to refuse a sale
 * the server would accept. On 2026-08-13 customers on a pre-v2.37.3 bundle
 * evaluated store hours on the UTC clock (the IST bug fixed above) and were hard
 * blocked here every morning while the store was open and taking orders.
 */
export function shouldBlockOrder({ isAdmin = false, statusConfirmed = false, status = null } = {}) {
  if (isAdmin) return false;
  if (!statusConfirmed) return false;
  return status?.open === false;
}
